// src/antdviews/components/tabs/LogListTab.tsx

import React from 'react'
import {
    Card,
    Table,
    Space,
    Tooltip,
    Row,
    Col,
    Typography,
    Select,
    Pagination,
} from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { FilePath } from '../../../services/Api'
import { getSourceName } from '../../../services/Macro'
import { mathUtil } from '../../../utils/MathUtil'

const { Text } = Typography

interface PhoneInfo {
    phoneMode: string
    phoneManufacturer: string
    phoneVersion: string
    phoneSDK: string
    hasPhoneMode: boolean
    hasPhoneManufacturer: boolean
    hasPhoneVersion: boolean
    hasPhoneSDK: boolean
}

interface UserItem {
    user_id: string
    name: string
    middleName?: string
    lastName?: string
    gender: number
    genderStr: string
    age: number
    birthday: string
    created: string
    online_portrait?: string
    yearBirth?: number
    monthBirth?: number
    dayBirth?: number
}

interface RecordItem {
    url: string
    fileName: string
    nickname: string
    device: string
    size: string
    startTime: string
    timeLength: string
    sourceStr: string
    description?: string
    href: string
    reports: Array<{ name: string; type: number }>
    decodeProgress: number
    rate: number
    meta: any
    location: number
    source: number
    reportTypes: number
}

interface LogItem {
    id: string
    date: string
    size: string
    updateTime: string
    source: number
    filePath: string
    fileName: string
    uuid: string
    rowspan: number
    hideDate?: boolean
}

interface DetailData {
    isActive: boolean
    accountName: string
    accountEmail: string
    onlinePortrait: string | null
    users: {
        list: UserItem[]
        totalPages?: number
        currentPage?: number
        totalRecords?: number
        pages?: number[]
    }
    logs: {
        list: LogItem[]
        totalPages?: number
        currentPage?: number
        totalRecords?: number
        pages?: number[]
    }
    records: {
        list: RecordItem[]
        totalPages?: number
        currentPage?: number
        totalRecords?: number
        pages?: number[]
    }
    phoneInfo: PhoneInfo
}

interface LogListTabProps {
    detail: DetailData
    trans: (key: string) => string
    changeLogList: (size: number, page: number) => void
}

interface LogListTabState {
    logsPerPage: number
    logsCurrentPage: number
}

export default class LogListTab extends React.Component<
    LogListTabProps,
    LogListTabState
> {
    constructor(props: LogListTabProps) {
        super(props)

        this.state = {
            logsPerPage: 10,
            logsCurrentPage: 1,
        }
    }

    /**
     * LPP: Logs Per Page
     *
     * 修改每页显示数量。
     */
    onLppChange = (value: number) => {
        const oldLogsPerPage = this.state.logsPerPage

        const newLogsCurrentPage = mathUtil.getNewPage(
            oldLogsPerPage,
            this.state.logsCurrentPage,
            value
        )

        this.setState(
            {
                logsPerPage: value,
                logsCurrentPage: newLogsCurrentPage,
            },
            () => {
                this.updateLogList()
            }
        )
    }

    /**
     * 请求日志列表。
     */
    updateLogList = () => {
        this.props.changeLogList(
            this.state.logsPerPage,
            this.state.logsCurrentPage
        )
    }

    /**
     * 分页切换。
     */
    onLogPageChange = (page: number) => {
        this.setState(
            {
                logsCurrentPage: page,
            },
            () => {
                this.updateLogList()
            }
        )
    }

    render() {
        const { detail } = this.props
        const t = this.props.trans
        const { logsPerPage, logsCurrentPage } = this.state

        const logCurP = detail.logs.currentPage || 1
        const logTotalP = detail.logs.totalPages || 0
        const logTotalR = detail.logs.totalRecords || 0
        const logList = detail.logs.list || []

        /**
         * 计算表格滚动高度。
         *
         * 与 RecordListTab 保持一致：
         * - Card 需要占满父容器
         * - 表格区域撑满剩余空间
         * - 数据较多时启用纵向滚动
         */
        const tableScrollY = window.innerHeight - 320
        const rowHeight = 56
        const headerHeight = 38
        const availableHeight = tableScrollY - headerHeight
        const totalContentHeight = logList.length * rowHeight
        const needScroll = totalContentHeight > availableHeight

        const columns: ColumnsType<LogItem> = [
            {
                title: t('date'),
                dataIndex: 'date',
                key: 'date',
                width: 120,
                ellipsis: true,
                render: (text, record) => {
                    if (record.hideDate) {
                        return null
                    }

                    return text
                },
                onCell: (record) => {
                    if (!record.hideDate) {
                        return {
                            rowSpan: record.rowspan,
                        }
                    }

                    return {
                        rowSpan: 0,
                    }
                },
            },
            {
                title: t('logId'),
                dataIndex: 'id',
                key: 'id',
                width: 180,
                ellipsis: true,
            },
            {
                title: t('size'),
                dataIndex: 'size',
                key: 'size',
                width: 80,
                ellipsis: true,
            },
            {
                title: t('updateTime'),
                dataIndex: 'updateTime',
                key: 'updateTime',
                width: 150,
                ellipsis: true,
            },
            {
                title: t('source'),
                key: 'source',
                width: 100,
                ellipsis: true,
                render: (_, record) => getSourceName(record.source),
            },
            {
                title: t('download'),
                key: 'download',
                width: 300,
                ellipsis: true,
                render: (_, record) => (
                    <Tooltip
                        title={record.fileName}
                        placement="topLeft"
                    >
                        <a
                            href={`${FilePath.LOGS}${record.filePath}`}
                            download={record.fileName}
                            style={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'bottom',
                            }}
                        >
                            <DownloadOutlined /> {record.fileName}
                        </a>
                    </Tooltip>
                ),
            },
        ]

        return (
            <Card
                title={t('logList')}
                style={{
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    background: 'var(--card-bg)',
                }}
                bodyStyle={{
                    padding: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                }}
            >
                {/* 表格区域 - 撑满剩余空间 */}
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Table
                        dataSource={logList}
                        rowKey={(record) => record.id}
                        pagination={false}
                        size="small"
                        columns={columns}
                        bordered
                        scroll={{
                            y: needScroll
                                ? tableScrollY > 200
                                    ? tableScrollY
                                    : 300
                                : undefined,
                        }}
                        style={{
                            borderRadius: 6,
                            height: '100%',
                        }}
                        tableLayout="fixed"
                    />
                </div>

                {/* 分页区域 - 固定在底部 */}
                <Row
                    justify="space-between"
                    align="middle"
                    style={{
                        marginTop: 16,
                        flexShrink: 0,
                        paddingTop: 8,
                        borderTop: '1px solid #f0f0f0',
                    }}
                >
                    <Col>
                        <Text type="secondary">
                            {t('table.footer.totalRecords') || '共'}{' '}
                            {logTotalR}{' '}
                            {t('table.footer.records') || '条记录'}
                            ，
                            {t('table.footer.currentPage') || '当前第'}{' '}
                            {logCurP}/{logTotalP}{' '}
                            {t('table.footer.page') || ''}
                        </Text>
                    </Col>

                    <Col>
                        <Space>
                            <span
                                style={{
                                    color: 'rgba(0,0,0,0.45)',
                                    fontSize: 14,
                                }}
                            >
                                {t('table.footer.pageSize') || '每页'}
                            </span>

                            <Select
                                value={logsPerPage}
                                onChange={this.onLppChange}
                                style={{
                                    width: 80,
                                }}
                            >
                                <Select.Option value={10}>10</Select.Option>
                                <Select.Option value={20}>20</Select.Option>
                                <Select.Option value={50}>50</Select.Option>
                                <Select.Option value={100}>100</Select.Option>
                            </Select>

                            <span
                                style={{
                                    color: 'rgba(0,0,0,0.45)',
                                    fontSize: 14,
                                }}
                            >
                                {t('table.footer.items') || '条'}
                            </span>

                            <Pagination
                                current={logsCurrentPage}
                                total={logTotalR}
                                pageSize={logsPerPage}
                                onChange={this.onLogPageChange}
                                showSizeChanger={false}
                                showQuickJumper
                                locale={{
                                    items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                    jump_to: t('table.footer.jumpTo') || '跳至',
                                    jump_to_confirm: t('table.footer.confirm') || '确定',
                                    page: t('table.footer.page') || '',
                                    prev_page: t('table.footer.prevPage') || '上一页',
                                    next_page: t('table.footer.nextPage') || '下一页',
                                    prev_5: t('table.footer.prev5') || '向前 5 页',
                                    next_5: t('table.footer.next5') || '向后 5 页',
                                    prev_3: t('table.footer.prev3') || '向前 3 页',
                                    next_3: t('table.footer.next3') || '向后 3 页',
                                }}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>
        )
    }
}